import { toFullName } from '@repo/toolkit/to-full-name'
import Checkbox from '@repo/ui/checkbox'
import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import React from 'react'

import { getPcrs } from '../../../api/pcr/get-pcrs'
import { getPeriods } from '../../../api/periods/get-periods'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  LanguageEnum,
  languageEnumMapping
} from '../../../lib/types/enums/languages/language.enum'
import {
  DiplomaEnum,
  diplomaEnumMapping
} from '../../../lib/types/enums/staff-application/diploma.enum'
import {
  EvalutationEnum,
  evalutationEnumMapping
} from '../../../lib/types/enums/staff-application/evalutation.enum'
import {
  StaffApplicationStatusEnum,
  staffApplicationStatusEnumMapping
} from '../../../lib/types/enums/staff-application/staff-application-status.enum'
import {
  StaffApplicationType,
  staffApplicationTypeMapping
} from '../../../lib/types/enums/staff-application/staff-application-type.enum'
import { StaffApplicationFilters } from '../../../lib/types/models/staff-application/staff-application'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'

type Props = {
  filter?: StaffApplicationFilters
  onFilterChange: (filter: StaffApplicationFilters) => void
}

const StaffApplicationsFilter: React.FC<Props> = ({ filter, onFilterChange }) => {
  const { t, currentLanguage } = useTranslate()
  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: pcrs } = useQuery({
    queryKey: ['pcrs'],
    queryFn: () => getPcrs({})
  })

  const languageMapping = languageEnumMapping()

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Trier par statut</Typography>
        <MultiSelectInput
          selectedOptions={filter?.statuses?.map((status) => ({
            label: staffApplicationStatusEnumMapping[status].label,
            value: status
          }))}
          variant="primary"
          onChange={(statuses) => {
            onFilterChange({ ...filter, statuses })
          }}
          options={Object.values(StaffApplicationStatusEnum).map((status) => ({
            label: staffApplicationStatusEnumMapping[status].label,
            value: status
          }))}
          label="Statut"
        />
      </div>

      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Date</Typography>
        <div className="flex space-x-4 justify-between">
          <DateStringInput
            language={currentLanguage}
            label="Date de début"
            value={filter?.fromDate}
            onChange={(date) => onFilterChange({ ...filter, fromDate: date })}
          />
          <DateStringInput
            language={currentLanguage}
            label="Date de fin"
            value={filter?.toDate}
            onChange={(date) => onFilterChange({ ...filter, toDate: date })}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Poste</Typography>
        <div className="flex flex-col gap-1">
          {Object.values(StaffApplicationType).map((type) => (
            <Checkbox
              key={type}
              checked={filter?.types?.includes(type)}
              label={staffApplicationTypeMapping[type].label}
              onChange={(checked) => {
                onFilterChange({
                  ...filter,
                  types: checked
                    ? [...(filter?.types || []), type]
                    : filter?.types?.filter((t) => t !== type)
                })
              }}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Disponibilités</Typography>
        <MultiSelectInput
          selectedOptions={filter?.periods?.map((period) => ({
            label: periods?.data.find((p) => p.id === period)?.name || '',
            value: period
          }))}
          searchable
          variant="primary"
          onChange={(periods) => {
            onFilterChange({ ...filter, periods })
          }}
          options={
            periods?.data.map((period) => ({
              label: period.name,
              value: period.id
            })) || []
          }
          label="Périodes"
        />
      </div>

      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Langues</Typography>
        <div className="flex flex-col gap-1">
          {Object.values(LanguageEnum).map((language) => (
            <Checkbox
              key={language}
              checked={filter?.languages?.includes(language)}
              label={languageMapping[language].label}
              onChange={(checked) => {
                onFilterChange({
                  ...filter,
                  languages: checked
                    ? [...(filter?.languages || []), language]
                    : filter?.languages?.filter((t) => t !== language)
                })
              }}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Trier par Diplômes</Typography>
        <MultiSelectInput
          selectedOptions={filter?.diplomas?.map((diploma) => ({
            label: t(diplomaEnumMapping[diploma]),
            value: diploma
          }))}
          variant="primary"
          onChange={(periods) => onFilterChange({ ...filter, diplomas: periods.map((p) => p) })}
          options={
            Object.values(DiplomaEnum).map((diploma) => ({
              label: t(diplomaEnumMapping[diploma]),
              value: diploma
            })) || []
          }
          label="Diplomes"
          searchable
        />
      </div>

      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">PCR</Typography>
        <SelectInput
          label="PCR"
          searchable
          options={
            pcrs?.data.map((pcr) => ({
              label: toFullName(pcr.firstName, pcr.lastName),
              value: pcr.id
            })) || []
          }
          selectedOption={getSelectedOption(
            filter?.pcrId,
            toFullName(
              pcrs?.data.find((pcr) => pcr.id === filter?.pcrId)?.firstName,
              pcrs?.data.find((pcr) => pcr.id === filter?.pcrId)?.lastName
            )
          )}
          onChange={(pcrId) => {
            onFilterChange({ ...filter, pcrId })
          }}
        />
      </div>

      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Évaluations</Typography>
        <div className="flex flex-col gap-1">
          {Object.values(EvalutationEnum).map((evaluation) => (
            <Checkbox
              key={evaluation}
              checked={filter?.evaluations?.includes(evaluation)}
              label={evalutationEnumMapping[evaluation].label}
              onChange={(checked) => {
                onFilterChange({
                  ...filter,
                  evaluations: checked
                    ? [...(filter?.evaluations || []), evaluation]
                    : filter?.evaluations?.filter((t) => t !== evaluation)
                })
              }}
            />
          ))}
        </div>
      </div>

      <div className="h-8" />
    </div>
  )
}

export default StaffApplicationsFilter
